// PRC-050 (boceto S6): textos y cálculos de presentación de la revisión de un
// cambio de precio. Las cifras vienen de get_pricing_price_change_review_v1;
// aquí solo se calculan variaciones y se redactan textos (sin afirmar causas).
import { formatNumber, formatPercent } from '../../../pricingJourney/formatNumber';
import type { PriceChangePeriod, PriceChangeReviewResponse, PriceChangeRhythm } from '../../types/priceChangeReview';

export const NO_CAUSALITY = 'Es lo observado en ventas; no demuestra que el cambio lo haya causado.';

export type PriceChangeAssessment = 'positive' | 'premature' | 'negative';

export const ASSESSMENT_OPTIONS: Array<{ id: PriceChangeAssessment; label: string; hint: string }> = [
  { id: 'positive', label: 'Resultado positivo', hint: 'Se cierra el seguimiento' },
  { id: 'premature', label: 'Evaluación prematura', hint: 'Se reprograma la revisión' },
  { id: 'negative', label: 'Resultado negativo', hint: 'Se abre una nueva evaluación de precio' },
];

/** Variación porcentual de «después» frente a «antes»; null si no hay base. */
export const pctChange = (before: number | null | undefined, after: number | null | undefined): number | null => {
  if (before == null || after == null || !Number.isFinite(before) || !Number.isFinite(after) || before === 0) return null;
  return ((after - before) / Math.abs(before)) * 100;
};

export const signedPct = (value: number | null): string =>
  value == null ? '—' : formatPercent(value, 1, { signed: true });

export const money = (value: number | null | undefined, currency: string | null | undefined, decimals = 2): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const symbol = !currency || currency === 'EUR' ? '€' : currency;
  return `${formatNumber(value, { decimals })} ${symbol}`;
};

/** «Habitual», «Fuera de su pauta» o «Sin pauta establecida» (criterio de Cadencia). */
export const rhythmLabel = (rhythm: PriceChangeRhythm | undefined): string => {
  if (!rhythm || !rhythm.established) return 'Sin pauta establecida';
  return rhythm.out_of_pattern ? 'Fuera de su pauta' : 'Habitual';
};

/** «cada 21 días · 3 días con compra» (frecuencia habitual = límite de Cadencia, nunca «P90»). */
export const rhythmNote = (rhythm: PriceChangeRhythm | undefined, after: PriceChangePeriod | undefined): string => {
  const days = after?.purchase_days ?? 0;
  const purchases = `${formatNumber(days, { decimals: 0 })} ${days === 1 ? 'día' : 'días'} con compra`;
  if (!rhythm?.established || rhythm.p90_interval_days == null) return `${purchases} desde el cambio`;
  return `cada ${Math.round(rhythm.p90_interval_days)} días · ${purchases}`;
};

/** Conclusión generada (editable) de la valoración. */
export const assessmentConclusion = (assessment: PriceChangeAssessment, review: PriceChangeReviewResponse | undefined): string => {
  if (!review || review.status !== 'ok' || !review.before || !review.after) {
    return assessment === 'premature'
      ? 'Evaluación prematura: todavía no hay ventas suficientes desde el cambio; se reprograma la revisión.'
      : '';
  }
  if (assessment === 'premature') {
    return `Evaluación prematura: ${review.window_days} días de ventas desde el cambio; se reprograma la revisión.`;
  }
  const { before, after, application } = review;
  const basePrice = before.charged_price;
  const direction = application && basePrice != null
    ? (application.applied_price > basePrice ? 'Subida aplicada' : application.applied_price < basePrice ? 'Bajada aplicada' : 'Precio mantenido')
    : 'Precio aplicado';
  const marginPart = before.margin == null || after.margin == null
    ? 'margen no calculable'
    : `margen ${signedPct(pctChange(before.margin, after.margin))}`;
  const units = pctChange(before.units, after.units);
  const unitsPart = before.units <= 0
    ? 'sin unidades comparables antes del cambio'
    : units == null || Math.abs(units) < 0.05
      ? 'unidades sin cambios'
      : units < 0
        ? `caída de unidades del ${formatPercent(Math.abs(units), 1)}`
        : `aumento de unidades del ${formatPercent(units, 1)}`;
  return `${direction}; ${marginPart} con ${unitsPart}.`;
};
